import { add, format, isAfter, startOfDay } from "date-fns";
import React, { useState } from "react";

import { GetFacility_facility_hoursOfOperation } from "/apollo/schema/types";
import { Text } from "/component/base";
import { ChevronDown, ChevronUp } from "/component/base/Icon";
import { layout } from "/styles";
import { parseDateTime } from "/util/date";
import { findNextOpenDay, getHoursForDay, isDayClosed, isFacilityOpen } from "/util/facility.util";

import {
  ChevronButton,
  HoursOfOperationWeeklyRowWrapper,
  HoursOfOperationWrapper,
} from "./HoursOfOperation.styles";

type Props = {
  canShowAllHours?: boolean;
  hoursOfOperation: GetFacility_facility_hoursOfOperation[] | null;
};

const HoursOfOperation: React.FC<Props> = ({ canShowAllHours = true, hoursOfOperation }) => {
  const [showAllHours, setShowAllHours] = useState(false);

  const today = new Date();
  const hoursToday = getHoursForDay(hoursOfOperation, today.getDay());

  const getClosingTime = (closingDate: Date) =>
    closingDate.getMinutes() === 0 ? format(closingDate, "h aa") : format(closingDate, "h:mm aa");

  const getNextOpeningTime = () => {
    if (!hoursOfOperation || !hoursToday) return null;

    // If today is closed, or we are already past today's closing time, the next
    // opening is on a later day. Otherwise it is still ahead of us today.
    const pastClosing =
      !isDayClosed(hoursToday) && isAfter(today, parseDateTime(hoursToday.closingTime as string));

    const startOffset = isDayClosed(hoursToday) || pastClosing ? 1 : 0;
    const next = findNextOpenDay(hoursOfOperation, today.getDay(), startOffset);

    if (!next) return null;

    const openingTime = parseDateTime(next.hours.openingTime as string);

    const nextOpeningDate = add(startOfDay(today), {
      days: next.daysAhead,
      hours: openingTime.getHours(),
      minutes: openingTime.getMinutes(),
    });

    return format(nextOpeningDate, openingTime.getMinutes() === 0 ? "E h aa" : "E h:mm aa");
  };

  const getDailyHours = (isOpen: boolean, closingDate: Date | null) => {
    const suffix = isOpen && closingDate ? `| Closes ${getClosingTime(closingDate)}` : null;

    if (suffix) {
      return (
        <Text color={"textPrimary"} variant={canShowAllHours ? "body1" : "body2"}>
          {suffix}
        </Text>
      );
    }

    const nextOpening = getNextOpeningTime();
    if (!nextOpening) return null;

    return (
      <Text color={"textPrimary"} variant={canShowAllHours ? "body1" : "body2"}>
        {`| Opens ${nextOpening}`}
      </Text>
    );
  };

  const getWeeklyHours = () => {
    if (!hoursOfOperation) return;

    const rotatedHoursOfOperation = [
      ...hoursOfOperation.slice(today.getDay(), hoursOfOperation.length),
      ...hoursOfOperation.slice(0, today.getDay()),
    ];

    return (
      <div css={layout.margin("condensed", "skip", "skip", "skip")}>
        {rotatedHoursOfOperation.map((hours, index) => {
          const closed = isDayClosed(hours);

          const hoursLabel = closed
            ? "Closed"
            : `${format(parseDateTime(hours.openingTime as string), "h:mm aa")} - ${format(
                parseDateTime(hours.closingTime as string),
                "h:mm aa",
              )}`;

          return (
            <HoursOfOperationWeeklyRowWrapper key={hours.dayOfWeek ?? index}>
              <Text
                color={index === 0 ? "textDefault" : "textPrimary"}
                css={{ textTransform: "capitalize" }}
                variant={index === 0 ? "body1Bold" : "body1"}
              >
                {hours.dayOfWeek?.toLowerCase()}
              </Text>

              <Text
                color={index === 0 ? "textDefault" : "textPrimary"}
                variant={index === 0 ? "body1Bold" : "body1"}
              >
                {hoursLabel}
              </Text>
            </HoursOfOperationWeeklyRowWrapper>
          );
        })}
      </div>
    );
  };

  if (!hoursOfOperation || !hoursToday) {
    return <></>;
  }

  const isOpen = isFacilityOpen(hoursOfOperation, today);
  const closingDateTime = isDayClosed(hoursToday)
    ? null
    : parseDateTime(hoursToday.closingTime as string);

  return (
    <div>
      <HoursOfOperationWrapper>
        <Text
          color={isOpen ? "statusPositive" : "statusNegative"}
          css={{ marginRight: 4 }}
          variant={canShowAllHours ? "body1Bold" : "body2Bold"}
        >
          {isOpen ? "Open" : "Closed"}
        </Text>
        {!showAllHours && getDailyHours(isOpen, closingDateTime)}

        {canShowAllHours && (
          <ChevronButton onClick={() => setShowAllHours(!showAllHours)} variant={"blank"}>
            {showAllHours ? (
              <ChevronUp color="textPrimary" size={16} />
            ) : (
              <ChevronDown color="textPrimary" size={16} />
            )}
          </ChevronButton>
        )}
      </HoursOfOperationWrapper>
      {showAllHours && getWeeklyHours()}
    </div>
  );
};

export default HoursOfOperation;
